import { EventsTypeEnum, ProductionEventType } from '__generated__/types';
import { dayjs } from 'common/dayjs';
import { useModal } from 'common/hooks/useModal';
import { AntButton } from 'components/ant/AntButton/AntButton';
import { AntCalendar } from 'components/ant/AntCalendar';
import { AntCard } from 'components/ant/AntCard/AntCard';
import { AntCol } from 'components/ant/AntCol';
import { AntForm, AntFormItem, useAntForm } from 'components/ant/AntForm/AntForm';
import { AntInput } from 'components/ant/AntInput';
import { AntRow } from 'components/ant/AntRow';
import { AntSpin } from 'components/ant/AntSpin/AntSpin';
import ContentWrapper from 'components/ContentWrapper';
import { Modal } from 'components/UI/Modal';
import { PageTitle } from 'components/UI/PageTitle';
import { RangePicker } from 'components/UI/RangePicker';
import type { Dayjs } from 'dayjs';
import { useProductionEventsCreateMutation } from 'graphql/mutations/__generated__/productionEventsCreate';
import { useProductionEventsDeleteMutation } from 'graphql/mutations/__generated__/productionEventsDelete';
import { useProductionEventsUpdateMutation } from 'graphql/mutations/__generated__/productionEventsUpdate';
import { useCalendarEventsQuery } from 'graphql/queries/__generated__/calendarEvents';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomCell } from './CustomCell';
import { CustomHeader } from './CustomHeader';

interface FormValues {
  id?: number;
  name: string;
  startDate: Dayjs;
  endDate: Dayjs;
}

export const CalendarPage: FC = () => {
  const { t } = useTranslation();
  const { visibleModal: visibleCreateModal, toggleVisibility: toggleVisibilityCreateModal } = useModal();
  const [form] = useAntForm<FormValues>();
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  // The shown year drives the variables, so moving into another year fetches it — no state update mid-render.
  const { data, loading, refetch } = useCalendarEventsQuery({
    variables: { year: currentDate.year() },
  });
  const [productionEventsCreate, { loading: productionEventsCreateLoading }] = useProductionEventsCreateMutation();
  const [productionEventsUpdate, { loading: productionEventsUpdateLoading }] = useProductionEventsUpdateMutation();
  const [productionEventsDelete] = useProductionEventsDeleteMutation();
  const [filters, setFilters] = useState<EventsTypeEnum[]>([
    EventsTypeEnum.PublicHolidayEvent,
    EventsTypeEnum.ProductionEvent,
    EventsTypeEnum.PoLeadtimeEvent,
  ]);

  const handleStartDateChange = (date: Dayjs | null) => {
    form.setFieldValue('startDate', date);
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    form.setFieldValue('endDate', date);
  };

  const handleSaveHoliday = async () => {
    const values = form.getFieldsValue(true) as FormValues;
    const attributes = { name: values.name, startDate: values.startDate.format(), endDate: values.endDate.format() };

    if (values.id) {
      await productionEventsUpdate({ variables: { input: { id: values.id, ...attributes } } });
    } else {
      await productionEventsCreate({
        variables: {
          input: { name: values.name, startDate: values.startDate.format(), endDate: values.endDate.format() },
        },
      });
    }

    void refetch();
    toggleVisibilityCreateModal();
    form.resetFields();
  };

  const handleProductionEventDelete = async (id: number) => {
    const { data } = await productionEventsDelete({
      variables: {
        input: {
          id: id,
        },
      },
    });

    if (data?.productionEventsDelete?.success) {
      void refetch();
    }
  };

  const handleEditProductionHoliday = (event: ProductionEventType) => {
    form.setFieldsValue({
      id: event.productionEventId,
      name: event.name,
      startDate: dayjs(event.startDate),
      endDate: dayjs(event.endDate),
    });
    toggleVisibilityCreateModal();
  };

  const getCustomCell = (value: Dayjs) => {
    const eventsInCurrentDate = data?.calendarEvents.filter(
      (event) =>
        value.isBetween(dayjs(event.startDate), dayjs(event.endDate), 'day', '[]') && filters?.includes(event.type),
    );

    return (
      <div>
        {eventsInCurrentDate?.map((event) => (
          <CustomCell
            key={`${event.name}-${event.startDate}-${event.endDate}`}
            event={event}
            onDeleteEvent={(id) => void handleProductionEventDelete(id)}
            onEditEvent={handleEditProductionHoliday}
          />
        ))}
      </div>
    );
  };

  const handleCloseCreateEventModal = () => {
    form.resetFields();
    toggleVisibilityCreateModal();
  };

  const getCustomHeader = ({ value }: { value: Dayjs; onChange: (date: Dayjs) => void }) => {
    return (
      <CustomHeader
        date={value}
        onAddActionClick={toggleVisibilityCreateModal}
        onFiltersChange={setFilters}
        filters={filters}
        setCurrentDate={setCurrentDate}
      />
    );
  };

  const handleSelectDate = (date: Dayjs) => {
    setCurrentDate(date);
    form.setFieldsValue({ startDate: date, endDate: date });
    toggleVisibilityCreateModal();
  };

  return (
    <ContentWrapper>
      <PageTitle>{t('calendar.calendar')}</PageTitle>
      <AntSpin spinning={loading} size="large">
        <AntCard>
          <AntCalendar
            value={currentDate}
            headerRender={getCustomHeader}
            cellRender={(value: Dayjs, info: { type: string; originNode: ReactNode }) => {
              if (info.type === 'date') return getCustomCell(value);

              return info.originNode;
            }}
            onSelect={handleSelectDate}
          />
        </AntCard>
      </AntSpin>
      <Modal
        width={500}
        open={visibleCreateModal}
        title={form.getFieldValue('id') ? t('calendar.editHoliday') : t('calendar.addHoliday')}
        footer={false}
        onCancel={handleCloseCreateEventModal}
        destroyOnHidden
        confirmLoading={productionEventsCreateLoading || productionEventsUpdateLoading}
      >
        <AntForm layout="vertical" form={form}>
          <AntFormItem label={t('calendar.holidayNameReason')} name="name">
            <AntInput />
          </AntFormItem>
          <AntFormItem shouldUpdate noStyle>
            {({ getFieldValue }) => (
              <RangePicker
                startDate={getFieldValue('startDate') as Dayjs}
                endDate={getFieldValue('endDate') as Dayjs}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
              />
            )}
          </AntFormItem>
          <AntRow gutter={8} className="m-t-sm">
            <AntCol span={12}>
              <AntButton size="large" block onClick={handleCloseCreateEventModal}>
                {t('commonPhrases.cancel')}
              </AntButton>
            </AntCol>
            <AntCol span={12}>
              <AntFormItem noStyle shouldUpdate>
                {({ getFieldValue }) => (
                  <AntButton
                    disabled={!getFieldValue('name') || !getFieldValue('startDate') || !getFieldValue('endDate')}
                    type="primary"
                    size={'large'}
                    block
                    onClick={() => void handleSaveHoliday()}
                  >
                    {t('commonPhrases.save')}
                  </AntButton>
                )}
              </AntFormItem>
            </AntCol>
          </AntRow>
        </AntForm>
      </Modal>
    </ContentWrapper>
  );
};
